<template>
  <div>
    <!-- Header Card -->
    <b-card class="rounded-lg mb-2">
      <b-card-header>
        <div class="d-flex justify-content-between align-items-center flex-wrap">
          <div>
            <h4 class="mb-0">
              {{ $t('accounting_transfers_view') || 'Transfer Details' }}
            </h4>
            <p
              v-if="item"
              class="text-muted mb-0"
            >
              {{ item.transfer_number }}
            </p>
          </div>
          <div class="d-flex align-items-center flex-wrap mt-1 mt-sm-0">
            <gl-journal-chip
              v-if="item"
              class="mr-1 mb-50"
              :journal-entry-id="item.journal_entry_id"
              :status="item.status"
            />
            <late-posting-badge
              v-if="item"
              class="mr-1 mb-50"
              :late="!!(item.is_late_posting || (item.journal_entry && item.journal_entry.is_late_posting))"
              :late-from-date="item.late_from_date || (item.journal_entry && item.journal_entry.late_from_date)"
            />
            <b-button
              v-if="item && item.status && item.status.value === 'pending' && hasPermission('approve transfers')"
              variant="success"
              class="mr-1"
              :disabled="processing"
              @click="approveTransfer"
            >
              {{ $t('accounting_transfers_approve') || 'Approve' }}
            </b-button>
            <b-button
              v-if="item && item.status && item.status.value === 'pending' && hasPermission('cancel transfers')"
              variant="danger"
              class="mr-1"
              :disabled="processing"
              @click="cancelTransfer"
            >
              {{ $t('accounting_transfers_cancel') || 'Cancel' }}
            </b-button>
            <b-button
              variant="secondary"
              @click="$router.go(-1)"
            >
              {{ $t('accounting_transfers_back') || 'Back' }}
            </b-button>
          </div>
        </div>
      </b-card-header>
    </b-card>

    <!-- Loading State -->
    <div
      v-if="loadItem"
      class="text-center py-5"
    >
      <b-spinner
        variant="primary"
        label="Spinning"
      />
      <p class="mt-2">
        {{ $t('accounting_transfers_loading') || 'Loading...' }}
      </p>
    </div>

    <!-- Transfer Details -->
    <div v-else-if="item">
      <!-- Summary Cards -->
      <b-row class="match-height mb-2">
        <b-col
          cols="12"
          md="6"
          lg="3"
        >
          <b-card class="text-center">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <h6 class="mb-0 text-muted">
                {{ $t('accounting_transfers_amount') || 'Amount' }}
              </h6>
              <feather-icon
                icon="DollarSignIcon"
                size="20"
                class="text-primary"
              />
            </div>
            <h3 class="font-weight-bolder mb-0">
              {{ formatCurrency(item.amount) }}
            </h3>
          </b-card>
        </b-col>
        <b-col
          cols="12"
          md="6"
          lg="3"
        >
          <b-card class="text-center">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <h6 class="mb-0 text-muted">
                {{ $t('accounting_transfers_status') || 'Status' }}
              </h6>
              <feather-icon
                icon="CheckCircleIcon"
                size="20"
                class="text-info"
              />
            </div>
            <h5 class="font-weight-bolder mb-0">
              <b-badge :variant="getStatusVariant(item.status && item.status.value)">
                {{ $i18n.locale === 'ar' ? (item.status && item.status.label_ar) || '' : (item.status && item.status.label) || '' }}
              </b-badge>
            </h5>
          </b-card>
        </b-col>
        <b-col
          cols="12"
          md="6"
          lg="3"
        >
          <b-card class="text-center">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <h6 class="mb-0 text-muted">
                {{ $t('accounting_transfers_created_at') || 'Created At' }}
              </h6>
              <feather-icon
                icon="CalendarIcon"
                size="20"
                class="text-success"
              />
            </div>
            <h6 class="font-weight-bolder mb-0">
              {{ formatDate(item.created_at) }}
            </h6>
          </b-card>
        </b-col>
        <b-col
          cols="12"
          md="6"
          lg="3"
        >
          <b-card class="text-center">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <h6 class="mb-0 text-muted">
                {{ $t('accounting_transfers_approved_at') || 'Approved At' }}
              </h6>
              <feather-icon
                icon="ClockIcon"
                size="20"
                class="text-warning"
              />
            </div>
            <h6 class="font-weight-bolder mb-0">
              {{ item.approved_at ? formatDate(item.approved_at) : '-' }}
            </h6>
          </b-card>
        </b-col>
      </b-row>

      <!-- Transfer Information -->
      <b-card class="mb-2">
        <b-card-header>
          <h5 class="mb-0">
            {{ $t('accounting_transfers_transfer_information') || 'Transfer Information' }}
          </h5>
        </b-card-header>
        <b-card-body>
          <b-row>
            <b-col
              cols="12"
              md="6"
            >
              <b-row class="mb-2">
                <b-col cols="6">
                  <strong>{{ $t('accounting_transfers_transfer_number') || 'Transfer Number' }}:</strong>
                </b-col>
                <b-col cols="6">
                  {{ item.transfer_number || '-' }}
                </b-col>
              </b-row>
              <b-row class="mb-2">
                <b-col cols="6">
                  <strong>{{ $t('accounting_transfers_from_account') || 'From Account' }}:</strong>
                </b-col>
                <b-col cols="6">
                  <div v-if="item.from_source">
                    <div><strong>{{ item.from_source.code }}</strong></div>
                    <div class="text-muted small">
                      {{ item.from_source.name }}
                    </div>
                    <div class="text-muted small">
                      {{ $t('accounting_accounts_balance') || 'Balance' }}: {{ formatCurrency(item.from_source.current_balance) }}
                    </div>
                  </div>
                  <span v-else>-</span>
                </b-col>
              </b-row>
              <b-row class="mb-2">
                <b-col cols="6">
                  <strong>{{ $t('accounting_transfers_amount') || 'Amount' }}:</strong>
                </b-col>
                <b-col cols="6">
                  {{ formatCurrency(item.amount) }}
                </b-col>
              </b-row>
            </b-col>
            <b-col
              cols="12"
              md="6"
            >
              <b-row class="mb-2">
                <b-col cols="6">
                  <strong>{{ $t('accounting_transfers_status') || 'Status' }}:</strong>
                </b-col>
                <b-col cols="6">
                  <b-badge :variant="getStatusVariant(item.status && item.status.value)">
                    {{ $i18n.locale === 'ar' ? (item.status && item.status.label_ar) || '' : (item.status && item.status.label) || '' }}
                  </b-badge>
                </b-col>
              </b-row>
              <b-row class="mb-2">
                <b-col cols="6">
                  <strong>{{ $t('accounting_transfers_to_account') || 'To Account' }}:</strong>
                </b-col>
                <b-col cols="6">
                  <div v-if="item.to_source">
                    <div><strong>{{ item.to_source.code }}</strong></div>
                    <div class="text-muted small">
                      {{ item.to_source.name }}
                    </div>
                    <div class="text-muted small">
                      {{ $t('accounting_accounts_balance') || 'Balance' }}: {{ formatCurrency(item.to_source.current_balance) }}
                    </div>
                  </div>
                  <span v-else>-</span>
                </b-col>
              </b-row>
              <b-row class="mb-2">
                <b-col cols="6">
                  <strong>{{ $t('accounting_transfers_created_at') || 'Created At' }}:</strong>
                </b-col>
                <b-col cols="6">
                  {{ formatDate(item.created_at) }}
                </b-col>
              </b-row>
            </b-col>
          </b-row>
          <b-row v-if="item.description">
            <b-col cols="12">
              <b-row class="mb-2">
                <b-col cols="12">
                  <strong>{{ $t('accounting_transfers_description') || 'Description' }}:</strong>
                </b-col>
                <b-col
                  cols="12"
                  class="mt-1"
                >
                  <p class="mb-0">
                    {{ item.description }}
                  </p>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>

      <!-- User Information -->
      <b-card>
        <b-card-header>
          <h5 class="mb-0">
            {{ $t('accounting_transfers_user_information') || 'User Information' }}
          </h5>
        </b-card-header>
        <b-card-body>
          <b-row>
            <b-col
              cols="12"
              md="6"
            >
              <b-row class="mb-2">
                <b-col cols="6">
                  <strong>{{ $t('accounting_transfers_created_by') || 'Created By' }}:</strong>
                </b-col>
                <b-col cols="6">
                  <div v-if="item.created_by_user">
                    <div>{{ item.created_by_user.name }}</div>
                    <div class="text-muted small">
                      {{ item.created_by_user.email }}
                    </div>
                  </div>
                  <span v-else>-</span>
                </b-col>
              </b-row>
            </b-col>
            <b-col
              v-if="item.approved_by_user"
              cols="12"
              md="6"
            >
              <b-row class="mb-2">
                <b-col cols="6">
                  <strong>{{ $t('accounting_transfers_approved_by') || 'Approved By' }}:</strong>
                </b-col>
                <b-col cols="6">
                  <div>
                    <div>{{ item.approved_by_user.name }}</div>
                    <div class="text-muted small">
                      {{ item.approved_by_user.email }}
                    </div>
                  </div>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>

      <!-- Activity Logs Section -->
      <b-row
        v-if="hasPermission('view activity_logs')"
        class="mt-3"
      >
        <b-col cols="12">
          <b-card header="سجل النشاطات">
            <ActivityLogs
              :model-type="'transfer'"
              :model-id="Number(transferId)"
              :show-filters="true"
              :per-page="15"
            />
          </b-card>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BButton,
  BRow,
  BCol,
  BBadge,
  BSpinner,
} from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ActivityLogs from '@/components/ActivityLogs.vue'
import GlJournalChip from '@/views/modules/ledger/components/GlJournalChip.vue'
import LatePostingBadge from '@/views/modules/ledger/components/LatePostingBadge.vue'

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BButton,
    BRow,
    BCol,
    BBadge,
    BSpinner,
    FeatherIcon,
    ToastificationContent,
    ActivityLogs,
    GlJournalChip,
    LatePostingBadge,
  },
  computed: {
    ...mapGetters({
      item: 'accounting/transfers/item',
      loadItem: 'accounting/transfers/loadItem',
    }),
    transferId() {
      return this.$route.params.id
    },
  },
  data() {
    return {
      processing: false,
    }
  },
  mounted() {
    this.loadTransfer()
  },
  methods: {
    hasPermission(permission) {
      const userPermissions = this.$store.getters['users/user_permissions']
      if (Array.isArray(userPermissions)) {
        return userPermissions.includes(permission)
      }
      return Object.values(userPermissions || {}).includes(permission)
    },
    formatCurrency(amount, currency = 'EGP') {
      return new Intl.NumberFormat('ar-EG', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
      }).format(amount)
    },
    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleDateString(this.$i18n.locale === 'ar' ? 'ar-EG' : 'en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    getStatusVariant(status) {
      const variants = {
        pending: 'warning',
        completed: 'success',
        cancelled: 'danger',
      }
      return variants[status] || 'secondary'
    },
    async approveTransfer() {
      if (!confirm(this.$t('accounting_transfers_confirm_approve') || 'Are you sure you want to approve this transfer?')) {
        return
      }
      this.processing = true
      try {
        await this.$store.dispatch('accounting/transfers/approve', this.transferId)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('accounting_transfers_messages.approved_success') || 'Transfer approved successfully',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        this.loadTransfer()
      } catch (error) {
        const errorMessage = error.response?.data?.message || this.$t('accounting_transfers_messages.error') || 'An error occurred'
        this.$toast({
          component: ToastificationContent,
          props: {
            title: errorMessage,
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.processing = false
      }
    },
    async cancelTransfer() {
      if (!confirm(this.$t('accounting_transfers_confirm_cancel') || 'Are you sure you want to cancel this transfer?')) {
        return
      }
      this.processing = true
      try {
        await this.$store.dispatch('accounting/transfers/cancel', this.transferId)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('accounting_transfers_messages.cancelled_success') || 'Transfer cancelled successfully',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        this.loadTransfer()
      } catch (error) {
        const errorMessage = error.response?.data?.message || this.$t('accounting_transfers_messages.error') || 'An error occurred'
        this.$toast({
          component: ToastificationContent,
          props: {
            title: errorMessage,
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.processing = false
      }
    },
    loadTransfer() {
      this.$store.dispatch('accounting/transfers/get', this.transferId)
    },
  },
}
</script>

<style lang="scss">
</style>
